import { CheckCircle2, AlertTriangle, XCircle, Info, Coins, Loader2 } from "lucide-react";
import { forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "./ui/tooltip";
import { fmtCredits, voiceFlagUrl } from "../lib/api";

// Badge de coste en créditos con tooltip explicativo atractivo.
// `freeLabel`: texto cuando `free` es true (por defecto "Gratis"). Existe para distinguir dos
// casos que NO son lo mismo: una acción genuinamente gratis (voz Edge TTS, análisis sin IA...)
// de una acción de PAGO que hoy no tiene coste NUEVO (voz premium ya narrada del todo, nada
// que sintetizar de nuevo) — mostrar "Gratis" en el segundo caso es cierto para esta pulsación
// pero engañoso sobre la naturaleza de la voz (bug real reportado: TabNarracion.jsx).
export const CostBadge = ({ seconds = 0, prefix = "", tip, free = false, freeLabel = "Gratis", dark = false }) => {
  const { t } = useTranslation();
  const base = dark
    ? "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
    : "bg-white/20 hover:bg-white/30 text-white";
  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`ml-1 inline-flex items-center gap-1 ${base} transition-colors rounded-md px-2 py-0.5 text-sm font-semibold cursor-help`}>
            {free ? freeLabel : (<><Coins size={14} /> {prefix ? prefix + " " : ""}{fmtCredits(seconds)} {t("common.credits", { count: Math.round(seconds) })}</>)}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[270px] bg-slate-900 text-white text-sm font-medium leading-snug px-3 py-2 rounded-xl shadow-2xl border border-white/10">
          {tip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};


export const STATUS_STYLES = {
  "Nuevo": "bg-slate-100 text-slate-700 border-slate-300",
  "En Edición": "bg-amber-50 text-amber-700 border-amber-300",
  "Listo para Renderizar": "bg-sky-50 text-sky-700 border-sky-300",
  "Renderizando": "bg-blue-50 text-blue-700 border-blue-300",
  "En cola": "bg-amber-50 text-amber-700 border-amber-300",
  "Completado": "bg-emerald-50 text-emerald-700 border-emerald-300",
  "Archivado": "bg-slate-100 text-slate-500 border-slate-300",
  "Generando guion": "bg-amber-50 text-amber-700 border-amber-300",
  "Generando audio": "bg-blue-50 text-blue-700 border-blue-300",
  "Produciendo": "bg-blue-50 text-blue-700 border-blue-300",
  "Esperando revisión": "bg-sky-50 text-sky-700 border-sky-300",
  "Error": "bg-red-50 text-red-700 border-red-300",
};

export const STATUS_LABELS = {
  "Listo para Renderizar": "Listo para crear el vídeo",
  "Renderizando": "Creando vídeo",
  "En cola": "En cola",
};

export function StatusBadge({ status, busy = false }) {
  const { t } = useTranslation();
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full border-2 text-sm font-bold ${STATUS_STYLES[status] || STATUS_STYLES["Nuevo"]}`}>
      {busy && <Loader2 size={14} className="animate-spin mr-1.5 shrink-0" aria-hidden="true" />}
      {t(`status.${status}`, { defaultValue: STATUS_LABELS[status] || status })}
    </span>
  );
}

const ALERT = {
  success: { cls: "bg-emerald-50 text-emerald-800 border-emerald-300", Icon: CheckCircle2 },
  warning: { cls: "bg-amber-50 text-amber-800 border-amber-300", Icon: AlertTriangle },
  error: { cls: "bg-red-50 text-red-800 border-red-300", Icon: XCircle },
  info: { cls: "bg-sky-50 text-sky-800 border-sky-300", Icon: Info },
};

export function Alert({ type = "info", children, testid }) {
  const { cls, Icon } = ALERT[type] || ALERT.info;
  return (
    <div data-testid={testid} className={`flex items-start gap-3 border-2 rounded-xl px-4 py-3 text-base font-medium ${cls}`}>
      <Icon size={24} strokeWidth={2.5} className="shrink-0 mt-0.5" />
      <div>{children}</div>
    </div>
  );
}

export const Card = forwardRef(function Card({ children, className = "", ...rest }, ref) {
  return <div ref={ref} {...rest} className={`bg-white border-2 border-slate-200 rounded-xl shadow-sm ${className}`}>{children}</div>;
});

export function SectionTitle({ children, sub }) {
  return (
    <div className="mb-6">
      <h2 className="text-2xl sm:text-3xl font-bold font-head text-slate-900">{children}</h2>
      {sub && <p className="text-lg text-slate-600 mt-1">{sub}</p>}
    </div>
  );
}

// Bandera del idioma/país de una voz de narración, como imagen real (no emoji — no se
// renderizaba de forma fiable en Windows/Chrome). Reutilizada en los 3 selectores de voz
// (config. de canal, pestaña Narración, modal 1-clic).
export function VoiceFlag({ voiceId, fallbackLang, voiceLang, className = "w-5 h-auto rounded-sm shadow-sm shrink-0" }) {
  return <img src={voiceFlagUrl(voiceId, fallbackLang, voiceLang)} alt="" className={className} />;
}

// Etiquetas de una voz (género/edad/tono) como chips uniformes — mismo patrón visual en los 3
// selectores de voz. El acento (si lo hay) se muestra aparte, junto al nombre/bandera, no aquí.
export function VoiceTags({ v }) {
  const tags = [v.gender && v.gender !== "—" ? v.gender : null, v.age, v.tone].filter(Boolean);
  if (!tags.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5 mt-1">
      {tags.map((tag, i) => (
        <span key={i} className="text-xs font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{tag}</span>
      ))}
    </div>
  );
}

export const btnPrimary =
  "min-h-[48px] px-4 sm:px-5 text-base font-bold rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed";
export const btnSecondary =
  "min-h-[48px] px-4 sm:px-5 text-base font-bold rounded-xl bg-slate-100 text-slate-900 border-2 border-slate-200 hover:border-slate-400 transition-all flex items-center justify-center gap-2";
